// The atlas mip chain, built by a blit (the runtime design §4).
//
// WebGL2 has `generateMipmap`. WebGPU has nothing of the kind: a mip level is
// a render target like any other, so the levels are DRAWN, each one from the
// level above it with a linear tap.
//
// This is the one WGSL in the runtime folder and it is no twin of a GLSL
// shader: it is a fullscreen triangle and a texture read, and it exists only
// because the API has no call for the job. Every other module takes its WGSL
// from the `ProgramSource` a pass publishes.
//
// The chain is rebuilt after every `setSource` and every `patch`, because a
// patched cell whose lower levels still hold the old ink bleeds at distance.

import { STAGE_FRAGMENT } from './flags';

/**
 * The blit.
 *
 * The vertex stage makes a triangle that covers the whole level from the
 * vertex index alone, so the pipeline needs no vertex buffer. `uv.y` is
 * flipped against the clip y, because clip y = +1 is texel row 0 of a WebGPU
 * attachment and the source is read in the same orientation it was written.
 */
const MIP_WGSL = `
@group(0) @binding(0) var src: texture_2d<f32>;
@group(0) @binding(1) var smp: sampler;
struct VsOut { @builtin(position) pos: vec4f, @location(0) uv: vec2f }
@vertex fn vs(@builtin(vertex_index) i: u32) -> VsOut {
  let p = vec2f(f32((i << 1u) & 2u), f32(i & 2u));
  var o: VsOut;
  o.pos = vec4f(p * 2.0 - 1.0, 0.0, 1.0);
  o.uv = vec2f(p.x, 1.0 - p.y);
  return o;
}
@fragment fn fs(v: VsOut) -> @location(0) vec4f {
  return textureSample(src, smp, v.uv);
}
`;

/** The format the atlas is built in. The blit renders into the same one. */
export const ATLAS_FORMAT: GPUTextureFormat = 'rgba8unorm';

/**
 * The one blit pipeline of a device.
 *
 * It is built on demand and kept, because the atlas patches a badge cell
 * every time a face lands and every patch rebuilds the chain.
 */
export class MipBlitter {
  private device: GPUDevice | null = null;
  private pipeline: GPURenderPipeline | null = null;
  private layout: GPUBindGroupLayout | null = null;
  private sampler: GPUSampler | null = null;

  /** Drops everything. The next `build` makes it again on the new device. */
  reset(): void {
    this.device = null;
    this.pipeline = null;
    this.layout = null;
    this.sampler = null;
  }

  /**
   * Draws every level below level 0 of one texture.
   *
   * The work is its OWN command encoder and its own submit, because the
   * atlas is baked outside the frame loop and must not wait for a frame.
   */
  build(device: GPUDevice, texture: GPUTexture, levels: number): void {
    if (levels <= 1) return;
    this.prepare(device);
    const pipeline = this.pipeline;
    const layout = this.layout;
    const sampler = this.sampler;
    if (!pipeline || !layout || !sampler) return;
    const encoder = device.createCommandEncoder({ label: 'fast: atlas mips' });
    for (let level = 1; level < levels; level++) {
      const group = device.createBindGroup({
        layout,
        entries: [
          {
            binding: 0,
            resource: texture.createView({ baseMipLevel: level - 1, mipLevelCount: 1 }),
          },
          { binding: 1, resource: sampler },
        ],
      });
      const pass = encoder.beginRenderPass({
        label: `fast: atlas mip ${level}`,
        colorAttachments: [{
          view: texture.createView({ baseMipLevel: level, mipLevelCount: 1 }),
          loadOp: 'clear',
          storeOp: 'store',
          clearValue: { r: 0, g: 0, b: 0, a: 0 },
        }],
      });
      pass.setPipeline(pipeline);
      pass.setBindGroup(0, group);
      pass.draw(3, 1);
      pass.end();
    }
    device.queue.submit([encoder.finish()]);
  }

  private prepare(device: GPUDevice): void {
    if (this.device === device && this.pipeline) return;
    this.device = device;
    this.sampler = device.createSampler({
      label: 'fast: mip blit',
      addressModeU: 'clamp-to-edge', addressModeV: 'clamp-to-edge',
      magFilter: 'linear', minFilter: 'linear',
    });
    this.layout = device.createBindGroupLayout({
      label: 'fast: mip blit',
      entries: [
        { binding: 0, visibility: STAGE_FRAGMENT, texture: { sampleType: 'float' } },
        { binding: 1, visibility: STAGE_FRAGMENT, sampler: { type: 'filtering' } },
      ],
    });
    const module = device.createShaderModule({ label: 'fast: mip blit', code: MIP_WGSL });
    this.pipeline = device.createRenderPipeline({
      label: 'fast: mip blit',
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.layout] }),
      vertex: { module, entryPoint: 'vs' },
      fragment: { module, entryPoint: 'fs', targets: [{ format: ATLAS_FORMAT }] },
      primitive: { topology: 'triangle-list' },
    });
  }
}
